<template>
  <article
    class="parking-editor-card"
    :class="{'parking-editor-card_collapsed': !expanded}"
    @click="handleCardClick"
  >
    <header class="parking-editor-card__header">
      <div class="parking-editor-card__heading">
        <span
          v-if="!expanded"
          class="parking-editor-card__heading-color"
          :style="{backgroundColor: selectedColor.hex}"
        />
        <span :class="{'parking-editor-card__creation-title': expanded}">
          {{ expanded ? expandedTitle : displayName }}
        </span>
      </div>

      <div class="parking-editor-card__header-actions">
        <button
          class="parking-editor-card__icon-button"
          type="button"
          aria-label="Удалить парковку"
          @click.stop="$emit('delete')"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24"><use xlink:href="#icon-trash" /></svg>
        </button>
        <button
          class="parking-editor-card__icon-button parking-editor-card__collapse"
          :class="{'parking-editor-card__collapse_open': expanded}"
          type="button"
          :aria-label="expanded ? 'Свернуть парковку' : 'Развернуть парковку'"
          :aria-expanded="String(expanded)"
          @click.stop="$emit('toggle')"
        >
          <span class="parking-editor-card__row-chevron" aria-hidden="true">›</span>
        </button>
      </div>
    </header>

    <div v-if="expanded" class="parking-editor-card__body">
      <div class="parking-editor-card__field" @click.stop="$emit('focus')">
        <SmartInputText
          :value="parking.name"
          caption="Название"
          placeholder="Например: Парковка со двора"
          maxlength="40"
          :default-if-empty="''"
          size="m"
          @input="updateParking({name: $event})"
          @focus="$emit('focus')"
        />
      </div>

      <div class="parking-editor-card__section">
        <div class="parking-editor-card__label">
          Цвет парковки
        </div>
        <div class="parking-editor-card__colors">
          <button
            v-for="color in colors"
            :key="color.value"
            class="parking-editor-card__color"
            :class="{'parking-editor-card__color_selected': color.value === parking.color}"
            :style="{'--parking-color': color.hex}"
            type="button"
            :aria-label="`Выбрать цвет ${color.value}`"
            @click.stop="updateParking({color: color.value})"
          />
        </div>
      </div>

      <button
        class="parking-editor-card__row"
        type="button"
        @click.stop="$emit('select-cameras')"
      >
        <span>
          <strong>Камеры</strong>
          <small>{{ 'Добавлено ' + camerasCountLabel }}</small>
        </span>
        <span class="parking-editor-card__row-chevron" aria-hidden="true">›</span>
      </button>

      <button
        class="parking-editor-card__row"
        type="button"
        @click.stop="$emit('edit-map')"
      >
        <span>
          <strong>Разметка на карте</strong>
          <small>{{ hasMapMarkup ? 'Выполнено' : 'Не выполнено' }}</small>
        </span>
        <span class="parking-editor-card__row-chevron" aria-hidden="true">›</span>
      </button>

      <div class="parking-editor-card__progress" aria-hidden="true">
        <i class="parking-editor-card__progress-line" />
        <span
          class="parking-editor-card__progress-point parking-editor-card__progress-point_name"
          :class="{'is-complete': hasName}"
        >✓</span>
        <span
          class="parking-editor-card__progress-point parking-editor-card__progress-point_cameras"
          :class="{'is-complete': hasCameras}"
        >✓</span>
        <span
          class="parking-editor-card__progress-point parking-editor-card__progress-point_map"
          :class="{'is-complete': hasMapMarkup}"
        >✓</span>
      </div>
    </div>
  </article>
</template>

<script>
import {isParkingPolygonValid} from "@/components/pacs/analytics/parkingDetection/parkingPolygon.js";

/**
 * Редактор одной парковки внутри парковочного комплекса.
 */
export default {
  name: "ParkingEditorCard",
  props: {
    parking: {
      type: Object,
      required: true,
    },
    colors: {
      type: Array,
      required: true,
    },
    expanded: {
      type: Boolean,
      default: true,
    },
    loadingCameras: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    selectedColor() {
      return this.colors.find(color => color.value === this.parking.color) || this.colors[0];
    },
    displayName() {
      return String(this.parking.name || "Новая парковка").trim();
    },
    expandedTitle() {
      return this.parking.id ? "Редактировать парковку" : "Создание парковки";
    },
    hasName() {
      return Boolean(String(this.parking.name || "").trim());
    },
    hasCameras() {
      return Array.isArray(this.parking.camera_numbers) && this.parking.camera_numbers.length > 0;
    },
    camerasCountLabel() {
      const count = this.hasCameras ? this.parking.camera_numbers.length : 0;
      return `${count} ${this.pluralizeCameras(count)}`;
    },
    hasMapMarkup() {
      return isParkingPolygonValid(this.parking.polygon);
    },
  },
  methods: {
    handleCardClick() {
      if (!this.expanded) this.$emit("toggle");
      this.$emit("focus");
    },
    updateParking(patch) {
      this.$emit("update", patch);
      this.$emit("focus");
    },
    pluralizeCameras(count) {
      const lastTwoDigits = count % 100;
      const lastDigit = count % 10;

      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return "камер";
      if (lastDigit === 1) return "камера";
      if (lastDigit >= 2 && lastDigit <= 4) return "камеры";
      return "камер";
    },
  },
};
</script>
